/**
 * FHIR Service — Frontend
 * Saves and retrieves FHIR R4 resources in the Firestore `fhir_resources` collection.
 *
 * Collection structure:
 *   fhir_resources/{ResourceType}/{resourceId}
 *
 * All saves are fire-and-forget (errors are caught & logged, never thrown to caller)
 * so that a FHIR failure never breaks the existing patient flow.
 */

import { db } from "../firebase.config";
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";
import { fetchWithAppCheck } from "../utils/fetchWithAppCheck";
import { logger } from "../utils/logger";

import {
  mapUserToFHIRPatient,
  mapVitalsToFHIRObservations,
  mapBPToFHIRObservation,
  mapHRToFHIRObservation,
  mapInsuranceToCoverage,
  mapChatSessionToEncounter,
  mapDiagnosisToCondition,
  mapPlanOfCareToCarePlan,
  mapMedicalReportToFHIR,
  mapPCPToPractitioner,
} from "../utils/fhirMapper";

const FHIR_COLLECTION = "fhir_resources";

// ─────────────────────────────────────────────
// CORE HELPERS
// ─────────────────────────────────────────────

/**
 * Generic save — writes any FHIR resource to Firestore.
 * Path: fhir_resources/{resourceType}/{resourceId}
 */
const saveFHIRResource = async (resourceType, resourceId, data) => {
  try {
    await setDoc(
      doc(db, FHIR_COLLECTION, resourceType, resourceId, "data"),
      data,
      { merge: true }
    );
  } catch (err) {
    console.error(`[FHIR] Failed to save ${resourceType}/${resourceId}:`, err);
  }
};

/**
 * Generic read — retrieves a FHIR resource from Firestore.
 */
export const getFHIRResource = async (resourceType, resourceId) => {
  try {
    const snap = await getDoc(
      doc(db, FHIR_COLLECTION, resourceType, resourceId, "data")
    );
    return snap.exists() ? snap.data() : null;
  } catch (err) {
    console.error(`[FHIR] Failed to get ${resourceType}/${resourceId}:`, err);
    return null;
  }
};

// ─────────────────────────────────────────────
// PATIENT
// ─────────────────────────────────────────────

/**
 * Save or update a FHIR Patient resource.
 * Call after createUser() or updateUser() in firestore.js.
 * @param {object} userData - fields from users/ collection
 * @param {string} userId
 */
export const saveFHIRPatient = async (userData, userId) => {
  const resource = mapUserToFHIRPatient(userData, userId);
  await saveFHIRResource("Patient", userId, resource);
};

/**
 * Retrieve a patient's FHIR Patient resource.
 */
export const getFHIRPatient = async (userId) => {
  return getFHIRResource("Patient", userId);
};

// ─────────────────────────────────────────────
// COVERAGE (INSURANCE)
// ─────────────────────────────────────────────

/**
 * Save a FHIR Coverage resource.
 * Call after createInsuranceData() in insuranceinfo.js.
 * @param {object} insuranceData - fields from insurance_data/ collection
 * @param {string} userId
 */
export const saveFHIRCoverage = async (insuranceData, userId) => {
  const resource = mapInsuranceToCoverage(insuranceData, userId);
  await saveFHIRResource("Coverage", `coverage-${userId}`, resource);
};

// ─────────────────────────────────────────────
// OBSERVATIONS — VITALS
// ─────────────────────────────────────────────

/**
 * Save FHIR Observation resources for height, weight, and BMI.
 * Call after calculateAndUpdateBMI() in pageone.js.
 * @param {object} vitalsData - { height, weight, bmi }
 * @param {string} userId
 */
export const saveFHIRObservations = async (vitalsData, userId) => {
  const observations = mapVitalsToFHIRObservations(vitalsData, userId);
  for (const obs of observations) {
    await saveFHIRResource("Observation", obs.id, obs);
  }
};

/**
 * Save FHIR Observation resources for blood pressure and heart rate.
 * Call after updatePhysicalExam (admin-operations) or equivalent frontend save.
 * @param {string|number} bpMm - systolic
 * @param {string|number} bpHg - diastolic
 * @param {string|number} heartrate
 * @param {string} userId
 * @param {string} sessionId
 */
export const saveFHIRVitalSignsFromExam = async (bpMm, bpHg, heartrate, userId, sessionId) => {
  if (bpMm && bpHg) {
    const bpObs = mapBPToFHIRObservation(bpMm, bpHg, userId, sessionId);
    await saveFHIRResource("Observation", bpObs.id, bpObs);
  }
  if (heartrate) {
    const hrObs = mapHRToFHIRObservation(heartrate, userId, sessionId);
    await saveFHIRResource("Observation", hrObs.id, hrObs);
  }
};

// ─────────────────────────────────────────────
// ENCOUNTER (CHATBOT SESSION)
// ─────────────────────────────────────────────

/**
 * Save or update a FHIR Encounter resource.
 * Call after createChatSession() and after completion updateChatSession().
 * @param {object} sessionData - fields from chatbot/ collection
 * @param {string} userId
 * @param {string} sessionId
 */
export const saveFHIREncounter = async (sessionData, userId, sessionId) => {
  const resource = mapChatSessionToEncounter(sessionData, userId, sessionId);
  await saveFHIRResource("Encounter", `encounter-${sessionId}`, resource);
};

/**
 * Update only the status and end time of an existing FHIR Encounter.
 * Call on chatbot session completion — avoids overwriting period.start.
 * @param {string} sessionId
 * @param {string} illness - for reasonCode
 */
export const completeFHIREncounter = async (sessionId, illness) => {
  try {
    await updateDoc(
      doc(db, FHIR_COLLECTION, "Encounter", `encounter-${sessionId}`, "data"),
      {
        status: "finished",
        "period.end": new Date().toISOString(),
        ...(illness ? { reasonCode: [{ text: illness }] } : {}),
      }
    );
  } catch (err) {
    console.error(`[FHIR] Failed to complete Encounter/encounter-${sessionId}:`, err);
  }
};

// ─────────────────────────────────────────────
// CONDITION (DIAGNOSIS)
// ─────────────────────────────────────────────

/**
 * Save a FHIR Condition resource from a diagnosis string.
 * Call after updateConditionalDiagnosis.
 * @param {string} diagnosis - conditional_diagnosis value
 * @param {string} userId
 * @param {string} sessionId
 */
export const saveFHIRCondition = async (diagnosis, userId, sessionId, illness = null) => {
  if (!diagnosis) return;
  const resource = mapDiagnosisToCondition(diagnosis, userId, sessionId, illness);
  await saveFHIRResource("Condition", `condition-${sessionId}`, resource);
};

// ─────────────────────────────────────────────
// CARE PLAN
// ─────────────────────────────────────────────

/**
 * Save a FHIR CarePlan resource from a plan of care string.
 * Call after updatePlanOfCare.
 * @param {string} planOfCare - plan_of_care value
 * @param {string} userId
 * @param {string} sessionId
 */
export const saveFHIRCarePlan = async (planOfCare, userId, sessionId) => {
  if (!planOfCare) return;
  const resource = mapPlanOfCareToCarePlan(planOfCare, userId, sessionId);
  await saveFHIRResource("CarePlan", `careplan-${sessionId}`, resource);
};

// ─────────────────────────────────────────────
// MEDICAL REPORTS (Allergy / Medication / Condition / Procedure / Document)
// ─────────────────────────────────────────────

/**
 * Save the correct FHIR resource based on the doc_type of a medical report.
 * Call after createMedicalReport() in pagetwo.js.
 * @param {object} reportData - fields from medical_reports/ collection
 * @param {string} userId
 */
export const saveFHIRFromMedicalReport = async (reportData, userId) => {
  const resource = mapMedicalReportToFHIR(reportData, userId);
  if (!resource) return;
  await saveFHIRResource(resource.resourceType, resource.id, resource);
};

// ─────────────────────────────────────────────
// PRACTITIONER (PCP)
// ─────────────────────────────────────────────

/**
 * Save a FHIR Practitioner resource from PCP details.
 * Call after upsertPCPPharmacyDetails() in pagetwo.js.
 * @param {object} pcpData - { pcp_name, pcp_phone, pcp_fax, pcp_address, pcp_email }
 * @param {string} userId
 */
export const saveFHIRPractitioner = async (pcpData, userId) => {
  if (!pcpData || !pcpData.pcp_name) return;
  const resource = mapPCPToPractitioner(pcpData, userId);
  await saveFHIRResource("Practitioner", `practitioner-${userId}`, resource);
};

// ─────────────────────────────────────────────
// FHIR BUNDLE DOWNLOAD (Phase 2)
// ─────────────────────────────────────────────

const FUNCTIONS_BASE = "https://us-central1-aigicare-prod.cloudfunctions.net";

/**
 * Admin: Fetch a complete FHIR R4 Bundle for a specific patient.
 * Calls getFHIRBundle Cloud Function.
 * @param {string} adminId   - Admin user ID (authorization + audit log)
 * @param {string} patientId - Patient user ID whose bundle to fetch
 * @returns {Promise<object|null>} FHIR Bundle object, or null on error
 */
export const downloadPatientFHIRBundle = async (adminId, patientId) => {
  try {
    const res = await fetchWithAppCheck(`${FUNCTIONS_BASE}/getFHIRBundle`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ admin_id: adminId, patient_id: patientId }),
    });
    if (!res.ok) {
      logger.error("[FHIR] getFHIRBundle failed", await res.json().catch(() => ({})));
      return null;
    }
    return await res.json();
  } catch (err) {
    logger.error("[FHIR] downloadPatientFHIRBundle error", err);
    return null;
  }
};

/**
 * Patient self-service: Fetch the calling patient's own FHIR R4 Bundle.
 * Calls getMyFHIRBundle Cloud Function.
 * @param {string} userId - The patient's own user ID
 * @returns {Promise<object|null>} FHIR Bundle object, or null on error
 */
export const downloadMyFHIRBundle = async (userId) => {
  try {
    const res = await fetchWithAppCheck(`${FUNCTIONS_BASE}/getMyFHIRBundle`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId }),
    });
    if (!res.ok) {
      logger.error("[FHIR] getMyFHIRBundle failed", await res.json().catch(() => ({})));
      return null;
    }
    return await res.json();
  } catch (err) {
    logger.error("[FHIR] downloadMyFHIRBundle error", err);
    return null;
  }
};
